import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { Logout as LogoutIcon, KeyboardArrowDown, MenuBook as DocumentationIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../context/NotificationContext';
import { roleAvatarColors } from '../../utils/roleColors';
import { getInitials } from '../../utils/getInitials';

const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  if (!user) return null;

  const initials = getInitials(user.firstName, user.lastName);
  const avatarColor = roleAvatarColors[user.role] || '#3b82f6';

  const handleDocumentation = () => {
    setAnchorEl(null);
    const basePath = user.role === 'admin' ? '/admin' : user.role === 'instructor' ? '/instructor' : '/student';
    navigate(`${basePath}/documentation`);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    showNotification('Logged out', 'info');
    if (user.role === 'admin') {
      navigate('/login');
    }
  };

  return (
    <>
      <Box
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: avatarColor,
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          {initials}
        </Avatar>
        <Typography
          variant="body2"
          sx={{ ml: 1, fontWeight: 500, display: { xs: 'none', sm: 'block' } }}
        >
          {user.firstName}
        </Typography>
        <KeyboardArrowDown sx={{ fontSize: 18, ml: 0.5, color: 'text.secondary' }} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: 2,
              boxShadow: (theme) => theme.shadows[3],
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {user.firstName} {user.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.email}
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Chip
              label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6875rem',
                fontWeight: 600,
                bgcolor: `${avatarColor}15`,
                color: avatarColor,
              }}
            />
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={handleDocumentation} sx={{ py: 1 }}>
          <ListItemIcon>
            <DocumentationIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Documentation</Typography>
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ py: 1 }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
